import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Wallet, CreditCard, CheckCircle, XCircle, Loader } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { id: 'pack_10', amount: 10, label: '10€' },
  { id: 'pack_25', amount: 25, label: '25€' },
  { id: 'pack_50', amount: 50, label: '50€' },
  { id: 'pack_100', amount: 100, label: '100€' },
  { id: 'pack_200', amount: 200, label: '200€' },
];

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'checking' | 'success' | 'error'
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loadingCustom, setLoadingCustom] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transactions`, { withCredentials: true });
      setTransactions(res.data.transactions || []);
    } catch {}
  }, []);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    
    setPaymentStatus('checking');
    let attempts = 0;
    const maxAttempts = 8;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus('error');
        return;
      }
      try {
        const res = await axios.get(`${API}/wallet/recharge/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid') {
          setPaymentStatus('success');
          await refreshUser();
          fetchTransactions();
          setSearchParams({});
          return;
        }
        if (res.data.status === 'expired') {
          setPaymentStatus('error');
          setSearchParams({});
          return;
        }
      } catch {}
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, refreshUser, fetchTransactions, setSearchParams]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleRecharge = async (packageId) => {
    setLoading(true);
    setSelectedPackage(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/wallet/recharge`, {
        package_id: packageId,
        origin_url: originUrl
      }, { withCredentials: true });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear la recarga');
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const handleCustomRecharge = async () => {
    const amount = parseFloat(customAmount);
    if (!amount || amount < 1) { alert('La cantidad minima es 1€'); return; }
    if (amount > 5000) { alert('La cantidad maxima es 5.000€'); return; }
    setLoadingCustom(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/wallet/recharge-custom`, {
        amount,
        origin_url: originUrl
      }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear la recarga');
    } finally {
      setLoadingCustom(false);
    }
  };

  return (
    <div className="p-6 md:p-10 pb-24 md:pb-10 max-w-5xl" data-testid="wallet-page">
      <div className="mb-10 animate-fade-in-up">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight uppercase text-white">Monedero</h1>
        <p className="text-zinc-500 mt-2">Recarga tu tarjeta del INFERNO</p>
      </div>

      {/* Payment status banner */}
      {paymentStatus === 'checking' && (
        <div className="bg-yellow-900/20 border border-yellow-600/30 p-4 mb-8 flex items-center gap-3 animate-fade-in-up">
          <Loader className="w-5 h-5 text-yellow-400 animate-spin" />
          <p className="text-yellow-400 text-sm">Verificando tu pago...</p>
        </div>
      )}
      {paymentStatus === 'success' && (
        <div data-testid="payment-success" className="bg-green-900/20 border border-green-600/30 p-4 mb-8 flex items-center gap-3 animate-fade-in-up">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <p className="text-green-400 text-sm">Pago completado. Tu saldo ha sido actualizado.</p>
        </div>
      )}
      {paymentStatus === 'error' && (
        <div data-testid="payment-error" className="bg-red-900/20 border border-red-600/30 p-4 mb-8 flex items-center gap-3 animate-fade-in-up">
          <XCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-400 text-sm">No se pudo verificar el pago. Contacta con nosotros si el problema persiste.</p>
        </div>
      )}

      {/* Balance card */}
      <div className="bg-diavel-card border border-white/5 p-8 mb-10 animate-fade-in-up animate-delay-100">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-red-900/20 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-diavel-red" />
          </div>
          <div>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Saldo actual</p>
            <p className="font-heading font-extrabold text-4xl text-white tracking-tight" data-testid="wallet-balance">
              {(user?.balance || 0).toFixed(2)}€
            </p>
          </div>
        </div>
      </div>

      {/* Recharge packages */}
      <div className="mb-10 animate-fade-in-up animate-delay-200">
        <h2 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6">Recargar saldo</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {PACKAGES.map(pkg => (
            <button
              key={pkg.id}
              onClick={() => handleRecharge(pkg.id)}
              disabled={loading}
              data-testid={`recharge-${pkg.id}`}
              className={`bg-diavel-card border p-6 text-center transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.3)] ${
                selectedPackage === pkg.id ? 'border-diavel-red' : 'border-white/5 hover:border-red-900/30'
              } disabled:opacity-50`}
            >
              {loading && selectedPackage === pkg.id ? (
                <Loader className="w-5 h-5 animate-spin mx-auto text-diavel-red" />
              ) : (
                <>
                  <CreditCard className="w-5 h-5 text-zinc-600 mx-auto mb-3" />
                  <p className="font-heading font-bold text-2xl text-white">{pkg.label}</p>
                </>
              )}
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="mt-6 bg-diavel-card border border-white/5 p-6">
          <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mb-3">O elige tu propia cantidad</p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <input
                data-testid="custom-amount-input"
                type="number" min="1" max="5000" step="0.01"
                value={customAmount}
                onChange={e => setCustomAmount(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 pr-8 text-white placeholder:text-zinc-600 outline-none transition-all duration-300"
                placeholder="Cantidad"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">€</span>
            </div>
            <button
              onClick={handleCustomRecharge}
              disabled={loadingCustom || !customAmount}
              data-testid="custom-recharge-btn"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] disabled:opacity-50 flex items-center gap-2 text-sm whitespace-nowrap"
            >
              {loadingCustom ? <Loader className="w-4 h-4 animate-spin" /> : 'Recargar'}
            </button>
          </div>
        </div>
      </div>

      {/* Transaction history */}
      <div className="bg-diavel-card border border-white/5 p-6 animate-fade-in-up animate-delay-300" data-testid="transaction-history">
        <h2 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6">Historial de movimientos</h2>
        {transactions.length === 0 ? (
          <p className="text-zinc-600 text-sm">Aún no tienes movimientos</p>
        ) : (
          <div className="space-y-3">
            {transactions.map((txn, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-sm text-white">{txn.description}</p>
                  <p className="font-mono text-xs text-zinc-600">
                    {new Date(txn.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`font-heading font-bold text-sm ${txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {txn.amount >= 0 ? '+' : ''}{txn.amount.toFixed(2)}€
                  </span>
                  {txn.balance_after !== undefined && (
                    <p className="font-mono text-xs text-zinc-600">Saldo: {txn.balance_after.toFixed(2)}€</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
